import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import useSWR, { useSWRConfig } from 'swr';
import { Modal, ModalVariant, toast } from '@/components';
import {
    ReportEventEditorFields,
    ReportEventFrameSelectStep,
    type ReportEventFrameSelectStepHandle,
} from '@/components/ReportEventEditor';
import {
    CACHE_KEYS,
    getReportEventCacheKey,
    getSourceCacheKey,
} from '@/constants/cache-keys';
import { useReportEvent } from '@/features/ReportEventView/hooks/useReportEvent';
import { reportEventVideoStartSeconds } from '@/features/ReportEventView/utils/reportEventVideoStartSeconds';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import { EngineService } from '@/services/engine';
import type { ReportEventDraftEdits } from '@/types/report';
import { SourceType } from '@/types/source';
import type { Frame } from '@/types/event';
import { appendThumbnailFrame } from '@/utils/report-event-thumbnails';

interface EditableReportEvent {
    id: string;
    sourceId: string;
    sourceType?: SourceType;
    promptText: string | null;
    description: string | null;
    thumbnailFrames: Frame[];
}

interface EditReportEventModalProps {
    isOpen: boolean;
    onClose: () => void;
    reportId: string;
    reportEvent: EditableReportEvent;
    onUpdated: () => void;
}

type Step = 'details' | 'addThumbnail';

function draftFromReportEvent(
    event: EditableReportEvent,
): ReportEventDraftEdits {
    return {
        promptText: event.promptText ?? '',
        description: event.description ?? '',
        thumbnails: event.thumbnailFrames.map((frame) => ({
            time: frame.time,
            bbox: [...frame.bbox] as [number, number, number, number],
        })),
    };
}

export function EditReportEventModal({
    isOpen,
    onClose,
    reportId,
    reportEvent,
    onUpdated,
}: EditReportEventModalProps) {
    const { t } = useTranslation(I18nNamespace.REPORTS);
    const { mutate } = useSWRConfig();
    const [step, setStep] = useState<Step>('details');
    const [managePopoverOpen, setManagePopoverOpen] = useState(false);
    const [addThumbnailSubmitDisabled, setAddThumbnailSubmitDisabled] =
        useState(true);
    const frameSelectStepRef = useRef<ReportEventFrameSelectStepHandle | null>(
        null,
    );

    const {
        reportEvent: reportEventDetail,
        isLoading: reportEventDetailLoading,
    } = useReportEvent(step === 'addThumbnail' ? reportEvent.id : '');

    const eventStartSeconds = reportEventDetail
        ? reportEventVideoStartSeconds(reportEventDetail)
        : 0;

    const {
        handleSubmit: submitForm,
        reset,
        watch,
        setValue,
        getValues,
        formState: { isDirty, isSubmitting },
    } = useForm<ReportEventDraftEdits>({
        defaultValues: draftFromReportEvent(reportEvent),
    });

    const promptText = watch('promptText');
    const description = watch('description');
    const thumbnails = watch('thumbnails');

    useEffect(() => {
        if (!isOpen) {
            setManagePopoverOpen(false);
            return;
        }
        reset(draftFromReportEvent(reportEvent));
        setStep('details');
    }, [isOpen, reportEvent, reset]);

    const {
        data: sourceForThumbnail,
        isLoading: sourceLoading,
        error: sourceError,
    } = useSWR(
        step === 'addThumbnail'
            ? getSourceCacheKey(reportEvent.sourceId)
            : null,
        () => EngineService.getSource(reportEvent.sourceId),
    );

    useEffect(() => {
        if (!sourceError) {
            return;
        }
        toast.error(t('detail.editEventModal.updateError'));
        setStep('details');
    }, [sourceError, t]);

    const onValidSubmit = async (values: ReportEventDraftEdits) => {
        const trimmedPrompt = values.promptText.trim();
        if (!trimmedPrompt) {
            return;
        }
        try {
            const updatedEvent = await EngineService.updateReportEvent(
                reportEvent.id,
                {
                    promptText: trimmedPrompt,
                    description: values.description.trim(),
                    thumbnailFrames: values.thumbnails,
                },
            );
            toast.success(t('detail.editEventModal.updateSuccess'));
            await mutate(
                (key) =>
                    Array.isArray(key) &&
                    key[0] === CACHE_KEYS.REPORT_EVENTS_PREFIX &&
                    key[1] === reportId,
            );
            await mutate(getReportEventCacheKey(reportEvent.id), updatedEvent, {
                revalidate: false,
            });
            onUpdated();
            onClose();
        } catch {
            toast.error(t('detail.editEventModal.updateError'));
        }
    };

    const handleStartAddThumbnail = () => {
        setManagePopoverOpen(false);
        setAddThumbnailSubmitDisabled(true);
        setStep('addThumbnail');
    };

    const handleCancelAddThumbnail = () => setStep('details');

    const handleThumbnailAdded = (payload: {
        time: number;
        bbox: [number, number, number, number];
    }) => {
        setValue(
            'thumbnails',
            appendThumbnailFrame(getValues('thumbnails'), payload),
            { shouldDirty: true },
        );
        setStep('details');
    };

    const modalTitle =
        step === 'details'
            ? t('detail.editEventModal.title')
            : t('detail.editEventModal.addThumbnailTitle');

    const submitDisabled =
        step !== 'details' || !promptText?.trim() || isSubmitting || !isDirty;

    const addThumbnailStepLoading = sourceLoading || reportEventDetailLoading;

    const showAddThumbnailFooter =
        step === 'addThumbnail' &&
        Boolean(sourceForThumbnail) &&
        !addThumbnailStepLoading;

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={modalTitle}
            onCancel={
                showAddThumbnailFooter
                    ? handleCancelAddThumbnail
                    : step === 'details'
                      ? onClose
                      : undefined
            }
            onSubmit={
                showAddThumbnailFooter
                    ? () => frameSelectStepRef.current?.confirm()
                    : step === 'details'
                      ? () => void submitForm(onValidSubmit)()
                      : undefined
            }
            submitText={
                showAddThumbnailFooter
                    ? t('eventEditor.confirmAddThumbnail')
                    : t('detail.editEventModal.submit')
            }
            submitDisabled={
                showAddThumbnailFooter
                    ? addThumbnailSubmitDisabled
                    : submitDisabled
            }
            submitInProgress={isSubmitting}
            variant={ModalVariant.DEFAULT}
            size="medium"
            onBack={
                step === 'addThumbnail' ? handleCancelAddThumbnail : undefined
            }
            closeOnOutsideClick
        >
            {step === 'details' ? (
                <ReportEventEditorFields
                    sourceId={reportEvent.sourceId}
                    thumbnails={thumbnails}
                    promptText={promptText}
                    description={description}
                    addThumbnailDisabled={
                        reportEvent.sourceType !== SourceType.FILE
                    }
                    managePopoverOpen={managePopoverOpen}
                    onManagePopoverOpenChange={setManagePopoverOpen}
                    onPromptTextChange={(value) =>
                        setValue('promptText', value, { shouldDirty: true })
                    }
                    onDescriptionChange={(value) =>
                        setValue('description', value, { shouldDirty: true })
                    }
                    onThumbnailsChange={(next) =>
                        setValue('thumbnails', next, { shouldDirty: true })
                    }
                    onStartAddThumbnail={handleStartAddThumbnail}
                />
            ) : addThumbnailStepLoading || !sourceForThumbnail ? (
                <div className="p-10 text-center text-grey-300">
                    {t('detail.addCustomEventModal.loadingSources')}
                </div>
            ) : (
                <ReportEventFrameSelectStep
                    ref={frameSelectStepRef}
                    source={sourceForThumbnail}
                    eventStartSeconds={eventStartSeconds}
                    onConfirm={handleThumbnailAdded}
                    onSubmitDisabledChange={setAddThumbnailSubmitDisabled}
                />
            )}
        </Modal>
    );
}
